import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRightLeft,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Eraser,
  FolderInput,
  KeyRound,
  Laptop,
  Layers,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  RefreshCw,
  Search,
  Trash2,
  Tv,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { adminApi } from "../../api/admin";
import { ApiError } from "../../api/client";
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Menu,
  type MenuItem,
  Modal,
  PageHeader,
  SearchInput,
  Segmented,
  Select,
  Skeleton,
  formatDateTime,
  formatRelative,
  useConfirm,
  useToast,
} from "../../ds";
import type { Device, DeviceGroup } from "../../types";
import { SCREEN_STATE, codeIsLive, deviceLabel, deviceShortId, errorText, isOnline, screenState, wantsReconnect } from "./labels";

type Filter = "all" | "online" | "offline" | "waiting" | "disabled";

const DAY_MS = 24 * 3600 * 1000;
const STATE_ORDER: Record<string, number> = { reconnect: 0, online: 1, "signed-out": 2, offline: 3, never: 4, pending: 5, disabled: 6, revoked: 7 };

function matches(d: Device, f: Filter): boolean {
  const s = screenState(d);
  switch (f) {
    case "all":
      return true;
    case "online":
      return s === "online";
    case "offline":
      return d.status === "active" && s !== "online";
    case "waiting":
      return d.status === "pending" || s === "reconnect";
    case "disabled":
      return d.status === "disabled" || d.status === "revoked";
  }
}

function ageMs(iso: string | null | undefined): number {
  return iso ? Date.now() - new Date(iso).getTime() : Infinity;
}

function minutesLeft(d: Device): number {
  const expires = d.activation_expires_at ? new Date(d.activation_expires_at).getTime() : new Date(d.updated_at).getTime() + 10 * 60000;
  return Math.max(1, Math.ceil((expires - Date.now()) / 60000));
}

function lastActivity(d: Device): number {
  return new Date(d.last_seen_at ?? d.updated_at).getTime();
}

function codeErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 404 && /code/i.test(e.message)) return "No TV is waiting with that code. Check the code on the TV screen and try again.";
    if (e.status === 410) return "That code has expired. On the TV, wait for a new code (or restart the app), then type it here.";
  }
  return errorText(e, "The TV couldn't be approved. Please try again.");
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/* ------------------------------------------------------------------ */
/* Approve: new screen / replace an existing screen / sign in again.  */
/* ------------------------------------------------------------------ */

type ApproveOutcome = { kind: "new" | "replace" | "reconnect"; device: Device; replaced?: Device; groupName: string };

function ApproveForm({
  groups,
  devices,
  expected,
  layout,
  codeRef,
  onDone,
  onCancel,
}: {
  groups: DeviceGroup[] | undefined;
  devices: Device[];
  expected?: Device;
  layout: "page" | "modal";
  codeRef?: RefObject<HTMLInputElement>;
  onDone: (o: ApproveOutcome) => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [mode, setMode] = useState<"new" | "replace">("new");
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [replaceId, setReplaceId] = useState("");
  const [search, setSearch] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const lookup = useQuery({
    queryKey: ["admin", "activation-lookup", code],
    queryFn: () => adminApi.lookupActivation(code),
    enabled: code.length === 6,
    retry: false,
    staleTime: 5000,
  });
  const found = code.length === 6 ? lookup.data : undefined;
  const reconnect = found?.kind === "reconnect";
  const groupName = useMemo(() => new Map((groups ?? []).map((g) => [g.id, g.name])), [groups]);

  // Prefill from what the code belongs to.
  const seenLookup = useRef<string | null>(null);
  useEffect(() => {
    if (!found || seenLookup.current === found.device.id + code) return;
    seenLookup.current = found.device.id + code;
    setSubmitError(null);
    if (found.kind === "reconnect") {
      setMode("new");
      setName(found.device.name ?? "");
      setGroupId(found.device.device_group_id ?? "");
    } else if (found.suggestions.length) {
      setReplaceId(found.suggestions[0].device_id);
    }
  }, [found, code]);

  useEffect(() => {
    if (!groupId && groups?.length === 1) setGroupId(groups[0].id);
  }, [groups, groupId]);

  const candidates = useMemo(() => {
    if (!found || reconnect) return [];
    const rank = new Map(found.suggestions.map((s, i) => [s.device_id, i]));
    const typed = name.trim().toLowerCase();
    return devices
      .filter((d) => d.status !== "pending" && d.id !== found.device.id)
      .map((d) => ({
        d,
        reason: found.suggestions.find((s) => s.device_id === d.id)?.reason ?? (typed && d.name?.trim().toLowerCase() === typed ? "Same name" : null),
        score: (rank.has(d.id) ? 100 - (rank.get(d.id) ?? 0) : 0) + (typed && d.name?.trim().toLowerCase() === typed ? 50 : 0) + (groupId && d.device_group_id === groupId ? 10 : 0),
      }))
      .sort((a, b) => b.score - a.score || lastActivity(b.d) - lastActivity(a.d));
  }, [devices, found, reconnect, name, groupId]);

  const visibleCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(({ d }) => [deviceLabel(d), deviceShortId(d), groupName.get(d.device_group_id ?? "") ?? "", d.last_ip ?? ""].join(" ").toLowerCase().includes(q));
  }, [candidates, search, groupName]);

  const typedName = name.trim().toLowerCase();
  const sameName = !reconnect && typedName ? candidates.find(({ d }) => d.name?.trim().toLowerCase() === typedName)?.d : undefined;
  const topSuggestion = found && !reconnect && found.suggestions.length ? devices.find((d) => d.id === found.suggestions[0].device_id) : undefined;
  const replaceTarget = mode === "replace" ? devices.find((d) => d.id === replaceId) : undefined;
  const replaceNeedsDetails = !!replaceTarget && (!replaceTarget.name || !replaceTarget.device_group_id);

  const approve = useMutation({
    mutationFn: () => {
      if (mode === "replace" && !reconnect)
        return adminApi.activateDevice({
          code,
          replace_device_id: replaceId,
          name: replaceNeedsDetails && name.trim() ? name.trim() : undefined,
          device_group_id: replaceNeedsDetails && groupId ? groupId : undefined,
        });
      return adminApi.activateDevice({ code, name: name.trim() || undefined, device_group_id: groupId || undefined });
    },
    onSuccess: (device) => {
      void qc.invalidateQueries({ queryKey: ["admin", "devices"] });
      void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
      const kind = reconnect ? "reconnect" : mode;
      onDone({ kind, device, replaced: kind === "replace" ? replaceTarget : undefined, groupName: groupName.get(device.device_group_id ?? "") ?? "" });
      setCode("");
      setName("");
      setReplaceId("");
      setSearch("");
      setMode("new");
      setSubmitError(null);
      seenLookup.current = null;
    },
    onError: (e) => setSubmitError(codeErrorText(e)),
  });

  const ready =
    !!found &&
    !approve.isPending &&
    (reconnect
      ? !!name.trim() && !!groupId
      : mode === "replace"
        ? !!replaceTarget && (!replaceNeedsDetails || (!!(replaceTarget.name || name.trim()) && !!(replaceTarget.device_group_id || groupId)))
        : !!name.trim() && !!groupId);
  const submit = () => {
    if (ready) approve.mutate();
  };

  const codeError = code.length === 6 && lookup.isError ? codeErrorText(lookup.error) : null;
  const noGroups = groups && groups.length === 0;
  const otherTv = expected && found && found.device.id !== expected.id;

  const codeField = (
    <Field
      label="Code shown on the TV"
      error={codeError ?? undefined}
      hint={codeError ? undefined : code.length === 6 && lookup.isFetching ? "Checking the code…" : "6 letters or numbers"}
    >
      <Input
        ref={codeRef}
        value={code}
        onChange={(e) => {
          setSubmitError(null);
          setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6));
        }}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder="K7M2QX"
        className="ds-mono"
        style={{ letterSpacing: "0.22em", fontSize: 16, fontWeight: 650, maxWidth: layout === "page" ? 220 : undefined }}
        aria-invalid={!!codeError}
        autoComplete="off"
        spellCheck={false}
      />
    </Field>
  );

  const groupSelect = (
    <Field label="Screen group" hint={noGroups ? <><Link to="/admin/device-groups">Create a screen group</Link> first.</> : "Decides which reports it shows."}>
      <Select value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={!groups || noGroups}>
        <option value="" disabled>
          {groups ? "Choose a group" : "Loading groups…"}
        </option>
        {groups?.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </Select>
    </Field>
  );
  const nameInput = (label = "Name this TV", hint = "Where is it? People will see this name.") => (
    <Field label={label} hint={hint}>
      <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="e.g. Board room – left screen" maxLength={120} />
    </Field>
  );

  let details: JSX.Element | null = null;
  if (found && reconnect) {
    details = (
      <div className="ds-stack" style={{ gap: 14 }}>
        <Banner tone="info" title={`“${deviceLabel(found.device)}” is signing in again`}>
          This screen was already set up — its sign-in was reset (token rotated, expired, or the browser lost it). Approving keeps its name, group and reports. Only approve if you can see this code on that screen.
        </Banner>
        <div className="ds-grid ds-grid--2">
          {nameInput("Name", "Change it here if you like.")}
          {groupSelect}
        </div>
      </div>
    );
  } else if (found) {
    details = (
      <div className="ds-stack" style={{ gap: 14 }}>
        <div className="ds-row" style={{ flexWrap: "wrap", gap: 12 }}>
          <Segmented<"new" | "replace">
            value={mode}
            onChange={(m) => {
              setMode(m);
              if (m === "replace" && !replaceId && candidates[0]) setReplaceId(candidates[0].d.id);
            }}
            options={[
              { value: "new", label: "New screen", icon: Tv },
              { value: "replace", label: "Replace an existing screen", icon: ArrowRightLeft, title: "Use this when a TV or laptop that was set up before shows a new code" },
            ]}
          />
          <span className="ds-small ds-muted">
            Waiting TV: ID <span className="ds-mono">{deviceShortId(found.device)}</span>
            {found.device.screen_resolution ? ` · ${found.device.screen_resolution.replace("x", " × ")}` : ""}
            {found.device.last_ip ? ` · ${found.device.last_ip}` : ""}
          </span>
        </div>

        {mode === "new" ? (
          <>
            {topSuggestion && !sameName ? (
              <Banner
                tone="info"
                title={`Is this “${deviceLabel(topSuggestion)}” again?`}
                action={<Button size="sm" icon={ArrowRightLeft} onClick={() => { setReplaceId(topSuggestion.id); setMode("replace"); }}>Replace it</Button>}
              >
                {found.suggestions[0].reason}. Replacing keeps one entry per screen instead of adding another.
              </Banner>
            ) : null}
            <div className="ds-grid ds-grid--2">
              {nameInput()}
              {groupSelect}
            </div>
            {sameName ? (
              <Banner
                tone="warning"
                title={`A screen called “${deviceLabel(sameName)}” already exists`}
                action={<Button size="sm" icon={ArrowRightLeft} onClick={() => { setReplaceId(sameName.id); setMode("replace"); }}>Replace it instead</Button>}
              >
                {groupName.get(sameName.device_group_id ?? "") ?? "No group"} · {SCREEN_STATE[screenState(sameName)].label.toLowerCase()}
                {sameName.last_seen_at ? `, seen ${formatRelative(sameName.last_seen_at)}` : ""}. If it's the same TV or laptop, replace it so you don't end up with two.
              </Banner>
            ) : null}
          </>
        ) : (
          <div className="ds-stack" style={{ gap: 10 }}>
            <p className="ds-small ds-muted" style={{ margin: 0 }}>
              The waiting TV takes over the screen you pick: its name, description, screen group and any reports assigned to that screen alone. The old entry is removed. Laptop browsers often need this — a different address or port (localhost vs 127.0.0.1) counts as a new screen.
            </p>
            <SearchInput value={search} onChange={setSearch} placeholder="Find the screen it replaces" width="100%" />
            <div role="radiogroup" aria-label="Screen to replace" style={{ maxHeight: layout === "modal" ? 240 : 280, overflow: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius-md)" }}>
              {visibleCandidates.length === 0 ? (
                <div className="ds-small ds-muted" style={{ padding: 14 }}>No screen matches “{search}”.</div>
              ) : (
                visibleCandidates.map(({ d, reason }) => {
                  const st = SCREEN_STATE[screenState(d)];
                  const on = d.id === replaceId;
                  return (
                    <label
                      key={d.id}
                      className="ds-list__row is-clickable"
                      style={{ padding: "9px 12px", background: on ? "var(--accent-soft, var(--hover))" : undefined }}
                    >
                      <input type="radio" name="replace-target" checked={on} onChange={() => setReplaceId(d.id)} style={{ accentColor: "var(--accent)" }} />
                      <div className="ds-list__main">
                        <div className="ds-list__title ds-truncate">{deviceLabel(d)}</div>
                        <div className="ds-list__meta ds-truncate">
                          {groupName.get(d.device_group_id ?? "") ?? "No group"} · ID {deviceShortId(d)} · {d.last_seen_at ? `seen ${formatRelative(d.last_seen_at)}` : `added ${formatRelative(d.created_at)}`}
                        </div>
                      </div>
                      {reason ? <Badge tone="accent">{reason}</Badge> : null}
                      <Badge tone={st.tone} dot>{st.label}</Badge>
                    </label>
                  );
                })
              )}
            </div>
            {replaceTarget && isOnline(replaceTarget) ? (
              <Banner tone="warning" title={`“${deviceLabel(replaceTarget)}” was online ${formatRelative(replaceTarget.last_seen_at)}`}>
                If that TV is still in use somewhere else it will be signed out. Choose “New screen” instead if these are two different screens.
              </Banner>
            ) : null}
            {replaceNeedsDetails ? (
              <div className="ds-grid ds-grid--2">
                {!replaceTarget?.name ? nameInput() : <div />}
                {!replaceTarget?.device_group_id ? groupSelect : <div />}
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  const approveLabel = reconnect ? "Sign it back in" : mode === "replace" ? "Replace screen" : "Approve TV";
  const actions = (
    <div className="ds-row" style={{ justifyContent: "flex-end", gap: 8 }}>
      {onCancel ? <Button onClick={onCancel}>Cancel</Button> : null}
      <Button variant="primary" icon={mode === "replace" && !reconnect ? ArrowRightLeft : CheckCircle2} loading={approve.isPending} disabled={!ready} onClick={submit}>
        {approveLabel}
      </Button>
    </div>
  );

  return (
    <div className="ds-stack" style={{ gap: 16 }}>
      {layout === "page" ? (
        <div className="ds-row" style={{ alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
          <div style={{ width: 220 }}>{codeField}</div>
          <p className="ds-small ds-muted" style={{ margin: "26px 0 0", flex: "1 1 280px", maxWidth: 560 }}>
            {found
              ? null
              : "Type the code and we'll show which TV it is — a brand-new screen, or one that was set up before and needs to sign in again. Laptop showing a new code? You'll be able to attach it to its existing entry instead of adding another."}
          </p>
        </div>
      ) : (
        codeField
      )}
      {otherTv ? (
        <Banner tone="info" title="That code belongs to a different waiting TV">
          It's the TV with ID {deviceShortId(found.device)}. You can still approve it here.
        </Banner>
      ) : null}
      {details}
      {submitError ? <Banner tone="danger" title="Not approved">{submitError}</Banner> : null}
      {found || layout === "modal" ? actions : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function WaitingCard({ d, onApprove, onDelete }: { d: Device; onApprove: () => void; onDelete: () => void }) {
  const live = codeIsLive(d);
  const reconnect = wantsReconnect(d);
  const specs = [d.screen_resolution ? d.screen_resolution.replace("x", " × ") + " screen" : null, d.app_version ? `App ${d.app_version}` : null, d.last_ip].filter(Boolean);
  return (
    <Card>
      <div className="ds-row" style={{ alignItems: "flex-start", justifyContent: "space-between" }}>
        <div className="ds-row" style={{ gap: 12, minWidth: 0 }}>
          <span className={live ? "ds-stat__icon ds-stat__icon--warning" : "ds-stat__icon"} style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0 }}>
            {reconnect ? <KeyRound size={18} /> : <Tv size={18} />}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 650 }} className="ds-truncate">{reconnect ? deviceLabel(d) : "New TV"}</div>
            <div className="ds-small ds-muted">
              {reconnect ? "Wants to sign in again · " : ""}ID <span className="ds-mono">{deviceShortId(d)}</span>
            </div>
          </div>
        </div>
        {live ? (
          <Badge tone="success" dot title="The code on this TV can be used right now.">
            Code on screen · {minutesLeft(d)} min left
          </Badge>
        ) : (
          <Badge title="Codes last 10 minutes. The TV shows a fresh one by itself when it's on the Reports screen.">Code expired</Badge>
        )}
      </div>
      <div className="ds-small ds-muted" style={{ marginTop: 12, display: "grid", gap: 4 }}>
        <span className="ds-row" style={{ gap: 6 }} title={formatDateTime(d.updated_at)}>
          <Clock size={13} /> Asked {formatRelative(d.updated_at)}
        </span>
        <span className="ds-row" style={{ gap: 6 }}>
          <Tv size={13} /> {specs.length ? specs.join(" · ") : "No device details reported"}
        </span>
      </div>
      <div className="ds-row" style={{ marginTop: 14, justifyContent: "flex-end", gap: 6 }}>
        {!reconnect ? (
          <Button size="sm" variant="ghost" icon={Trash2} onClick={onDelete} aria-label="Delete this request">
            Delete
          </Button>
        ) : null}
        <Button size="sm" variant={live ? "primary" : "secondary"} icon={CheckCircle2} onClick={onApprove}>
          {reconnect ? "Sign in again…" : "Approve…"}
        </Button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function EditScreenModal({ device, groups, focus, onClose }: { device: Device | null; groups: DeviceGroup[]; focus: "name" | "group"; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [groupId, setGroupId] = useState("");
  useEffect(() => {
    if (!device) return;
    setName(device.name ?? "");
    setDescription(device.description ?? "");
    setGroupId(device.device_group_id ?? "");
  }, [device]);

  const save = useMutation({
    mutationFn: () => {
      const d = device as Device;
      const body: { name?: string; description?: string; device_group_id?: string } = {};
      if (name.trim() !== (d.name ?? "")) body.name = name.trim();
      if (description.trim() !== (d.description ?? "")) body.description = description.trim();
      if (groupId && groupId !== d.device_group_id) body.device_group_id = groupId;
      return adminApi.updateDevice(d.id, body);
    },
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["admin", "devices"] });
      void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
      toast.success("Screen updated", `“${deviceLabel(d)}” is in ${groups.find((g) => g.id === d.device_group_id)?.name ?? "its group"}.`);
      onClose();
    },
    onError: (e) => toast.error("Couldn't save the screen", errorText(e)),
  });
  const moved = !!device && !!groupId && groupId !== device.device_group_id;
  const groupField = (
    <Field label="Screen group" hint={moved ? "It switches to that group's reports within a minute." : "Decides which reports it shows."}>
      <Select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
        <option value="" disabled>Choose a group</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>{g.name}</option>
        ))}
      </Select>
    </Field>
  );
  return (
    <Modal
      open={!!device}
      onClose={onClose}
      title={focus === "group" ? "Move to another screen group" : "Edit screen"}
      description={device ? `ID ${deviceShortId(device)}${device.screen_resolution ? ` · ${device.screen_resolution.replace("x", " × ")}` : ""}` : undefined}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="ds-stack">
        {focus === "group" ? groupField : null}
        <Field label="Name" hint="Where is it? People will see this name.">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} onKeyDown={(e) => e.key === "Enter" && name.trim() && save.mutate()} />
        </Field>
        <Field label="Description" hint="Optional — e.g. which wall, or who looks after it.">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
        </Field>
        {focus === "group" ? null : groupField}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */

type CleanupKey = "stalePending" | "revoked" | "notSeen" | "duplicates";

function cleanupSets(all: Device[]): Record<CleanupKey, Device[]> {
  const stalePending = all.filter((d) => d.status === "pending" && !codeIsLive(d) && ageMs(d.updated_at) > DAY_MS);
  const revoked = all.filter((d) => d.status === "revoked");
  const notSeen = all.filter(
    (d) => (d.status === "active" || d.status === "disabled") && (d.last_seen_at ? ageMs(d.last_seen_at) > 30 * DAY_MS : ageMs(d.created_at) > 30 * DAY_MS),
  );
  const byName = new Map<string, Device[]>();
  for (const d of all) {
    if (d.status !== "active" && d.status !== "disabled") continue;
    const key = d.name?.trim().toLowerCase();
    if (!key) continue;
    byName.set(key, [...(byName.get(key) ?? []), d]);
  }
  const duplicates: Device[] = [];
  for (const list of byName.values()) {
    if (list.length < 2) continue;
    const [, ...older] = [...list].sort((a, b) => lastActivity(b) - lastActivity(a));
    duplicates.push(...older.filter((d) => !isOnline(d)));
  }
  return { stalePending, revoked, notSeen, duplicates };
}

const CLEANUP_INFO: Record<CleanupKey, { label: string; hint: string }> = {
  stalePending: { label: "Old requests", hint: "Waiting for approval for more than a day, code expired. A TV that's switched on simply asks again." },
  revoked: { label: "Revoked screens", hint: "Access was removed for good. Deleting lets that TV join again as a new screen." },
  duplicates: { label: "Older copies of the same name", hint: "Keeps the most recently active screen of each name (e.g. several “Local Dev Browser” entries)." },
  notSeen: { label: "Not seen for 30 days", hint: "Approved, but hasn't checked in for a month (or ever, if added more than a month ago)." },
};

function CleanupModal({ open, all, onClose, onReview, onDelete }: { open: boolean; all: Device[]; onClose: () => void; onReview: (ids: string[]) => void; onDelete: (list: Device[]) => void }) {
  const sets = useMemo(() => cleanupSets(all), [all]);
  const [picked, setPicked] = useState<Record<CleanupKey, boolean>>({ stalePending: true, revoked: true, duplicates: false, notSeen: false });
  const chosen = useMemo(() => {
    const m = new Map<string, Device>();
    (Object.keys(picked) as CleanupKey[]).forEach((k) => picked[k] && sets[k].forEach((d) => m.set(d.id, d)));
    return [...m.values()].sort((a, b) => deviceLabel(a).localeCompare(deviceLabel(b)));
  }, [picked, sets]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Clean up the screen list"
      description="Pick what to remove. Nothing is deleted until you confirm."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button disabled={!chosen.length} onClick={() => onReview(chosen.map((d) => d.id))}>Select in list to review</Button>
          <Button variant="danger" icon={Trash2} disabled={!chosen.length} onClick={() => onDelete(chosen)}>
            Delete {plural(chosen.length, "screen")}
          </Button>
        </>
      }
    >
      <div className="ds-stack" style={{ gap: 10 }}>
        {(Object.keys(CLEANUP_INFO) as CleanupKey[]).map((k) => (
          <label key={k} className="ds-card" style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", boxShadow: "none", cursor: sets[k].length ? "pointer" : "default", opacity: sets[k].length ? 1 : 0.6 }}>
            <input type="checkbox" checked={picked[k] && sets[k].length > 0} disabled={!sets[k].length} onChange={(e) => setPicked((p) => ({ ...p, [k]: e.target.checked }))} style={{ marginTop: 3, width: 16, height: 16, accentColor: "var(--accent)" }} />
            <div className="ds-grow">
              <div style={{ fontWeight: 600 }}>
                {CLEANUP_INFO[k].label} <span className="ds-muted" style={{ fontWeight: 500 }}>· {sets[k].length}</span>
              </div>
              <div className="ds-small ds-muted">{CLEANUP_INFO[k].hint}</div>
            </div>
          </label>
        ))}
        <div className="ds-small ds-muted" style={{ marginTop: 6 }}>{chosen.length ? `${plural(chosen.length, "screen")} will be deleted:` : "Nothing selected."}</div>
        {chosen.length ? <NameList devices={chosen} /> : null}
      </div>
    </Modal>
  );
}

function NameList({ devices, groupName }: { devices: Device[]; groupName?: Map<string, string> }) {
  return (
    <ul style={{ margin: 0, padding: "8px 12px 8px 28px", maxHeight: 220, overflow: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", fontSize: 13 }}>
      {devices.map((d) => (
        <li key={d.id} style={{ padding: "2px 0" }}>
          <span style={{ fontWeight: 600, color: "var(--text)" }}>{deviceLabel(d)}</span>
          <span className="ds-faint">
            {" "}· {SCREEN_STATE[screenState(d)].label}
            {groupName && d.device_group_id ? ` · ${groupName.get(d.device_group_id) ?? ""}` : ""} · ID {deviceShortId(d)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ */

export function DevicesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const codeRef = useRef<HTMLInputElement>(null);

  const devices = useQuery({ queryKey: ["admin", "devices"], queryFn: adminApi.listDevices, refetchInterval: 15_000 });
  const groups = useQuery({ queryKey: ["admin", "device-groups"], queryFn: adminApi.listDeviceGroups });

  const FILTERS: Filter[] = ["all", "online", "offline", "waiting", "disabled"];
  const [filter, setFilter] = useState<Filter>(() => (FILTERS.includes(params.get("filter") as Filter) ? (params.get("filter") as Filter) : "all"));
  const [groupFilter, setGroupFilter] = useState(params.get("group") ?? "");
  const [search, setSearch] = useState("");
  const [showOlderPending, setShowOlderPending] = useState(false);
  const [approving, setApproving] = useState<Device | null>(null);
  const [editing, setEditing] = useState<{ d: Device; focus: "name" | "group" } | null>(null);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (filter === "all") next.delete("filter");
    else next.set("filter", filter);
    if (groupFilter) next.set("group", groupFilter);
    else next.delete("group");
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [filter, groupFilter, params, setParams]);

  const all = useMemo(() => devices.data ?? [], [devices.data]);
  const loadFailed = devices.isError && !devices.data;
  const groupName = useMemo(() => new Map((groups.data ?? []).map((g) => [g.id, g.name])), [groups.data]);

  // Drop selections for rows that no longer exist.
  useEffect(() => {
    if (!devices.data) return;
    const ids = new Set(devices.data.map((d) => d.id));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [devices.data]);

  const waiting = useMemo(
    () => all.filter((d) => d.status === "pending" || (wantsReconnect(d) && codeIsLive(d))).sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [all],
  );
  const liveCodes = waiting.filter(codeIsLive).length;
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: all.length, online: 0, offline: 0, waiting: 0, disabled: 0 };
    for (const d of all) (["online", "offline", "waiting", "disabled"] as Filter[]).forEach((f) => matches(d, f) && c[f]++);
    return c;
  }, [all]);
  const approved = all.filter((d) => d.status === "active").length;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((d) => matches(d, filter))
      .filter((d) => !groupFilter || d.device_group_id === groupFilter)
      .filter((d) => {
        if (!q) return true;
        const hay = [deviceLabel(d), d.device_uuid, deviceShortId(d), d.last_ip ?? "", d.app_version ?? "", groupName.get(d.device_group_id ?? "") ?? "", d.description ?? ""].join(" ").toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => STATE_ORDER[screenState(a)] - STATE_ORDER[screenState(b)] || deviceLabel(a).localeCompare(deviceLabel(b), undefined, { sensitivity: "base" }));
  }, [all, filter, groupFilter, search, groupName]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "devices"] });
    void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
    void qc.invalidateQueries({ queryKey: ["reports"] });
  };

  const act = useMutation({
    mutationFn: ({ kind, d }: { kind: "disable" | "enable" | "revoke" | "rotate"; d: Device }) =>
      kind === "disable"
        ? adminApi.disableDevice(d.id)
        : kind === "enable"
          ? adminApi.enableDevice(d.id)
          : kind === "revoke"
            ? adminApi.revokeDevice(d.id)
            : adminApi.rotateDeviceToken(d.id),
    onSuccess: (_r, { kind, d }) => {
      invalidate();
      const label = deviceLabel(d);
      if (kind === "disable") toast.success("Screen disabled", `“${label}” has stopped showing reports. You can enable it again at any time.`);
      else if (kind === "enable") toast.success("Screen enabled", `“${label}” can show reports again. If the TV shows a code, approve it under “Waiting for approval”.`);
      else if (kind === "revoke") toast.success("Access revoked", `“${label}” can no longer show reports.`);
      else toast.success("Sign-in reset", `“${label}” will show a new code when its current session ends. Approve it to sign it back in.`);
    },
    onError: (e) => toast.error("That didn't work", errorText(e)),
  });

  const remove = useMutation({
    mutationFn: async (list: Device[]) => {
      if (list.length === 1) {
        await adminApi.deleteDevice(list[0].id);
        return { deleted: [list[0].id], not_found: [] as string[] };
      }
      return adminApi.bulkDeleteDevices(list.map((d) => d.id));
    },
    onSuccess: (r, list) => {
      invalidate();
      setSelected(new Set());
      const n = r.deleted.length;
      toast.success(n === 1 ? "Screen deleted" : `${n} screens deleted`, n === 1 ? `“${deviceLabel(list.find((d) => d.id === r.deleted[0]) ?? list[0])}” was removed from the list.` : "They were removed from the list with their single-screen assignments.");
    },
    onError: (e) => toast.error("Couldn't delete", errorText(e)),
  });

  const confirmDelete = async (list: Device[]) => {
    if (!list.length) return;
    const activeOnes = list.filter((d) => d.status === "active");
    const onlineOnes = list.filter(isOnline);
    const ok = await confirm({
      title: list.length === 1 ? `Delete “${deviceLabel(list[0])}”?` : `Delete ${list.length} screens?`,
      message: (
        <div className="ds-stack" style={{ gap: 10 }}>
          {list.length > 1 ? <NameList devices={list} groupName={groupName} /> : null}
          <div>
            {list.length === 1 ? "It's" : "They're"} removed from the list, together with any reports assigned to {list.length === 1 ? "that screen" : "those screens"} alone. Group assignments are not affected. This can't be undone.
          </div>
          {activeOnes.length ? (
            <div style={{ color: "var(--warning)" }}>
              {onlineOnes.length
                ? `${plural(onlineOnes.length, "screen")} ${onlineOnes.length === 1 ? "is" : "are"} online right now and will stop showing reports.`
                : `${plural(activeOnes.length, "screen")} ${activeOnes.length === 1 ? "is" : "are"} approved.`}{" "}
              A TV that's still switched on shows a new code and can be approved again.
            </div>
          ) : null}
        </div>
      ),
      confirmLabel: list.length === 1 ? "Delete screen" : `Delete ${list.length} screens`,
      tone: "danger",
    });
    if (ok) remove.mutate(list);
  };

  const menuFor = (d: Device): MenuItem[] => {
    const label = deviceLabel(d);
    const items: MenuItem[] = [];
    const divider: MenuItem = { label: "", divider: true, onSelect: () => undefined };
    if (d.status === "pending" || (wantsReconnect(d) && codeIsLive(d)))
      items.push({ label: d.status === "pending" ? "Approve…" : "Approve sign-in…", icon: CheckCircle2, onSelect: () => setApproving(d) });
    if (d.status !== "pending") {
      items.push(
        { label: "Rename…", icon: Pencil, onSelect: () => setEditing({ d, focus: "name" }) },
        { label: "Move to group…", icon: FolderInput, onSelect: () => setEditing({ d, focus: "group" }) },
      );
    }
    if (d.status === "active")
      items.push(divider, {
        label: "Reset sign-in…",
        icon: KeyRound,
        onSelect: async () => {
          if (
            await confirm({
              title: `Reset the sign-in for “${label}”?`,
              message: "Use this if you think this screen's sign-in was copied. The TV keeps working until its current session ends (up to 12 hours), then shows a new code. Approve that code here and it's back — same name, group and reports.",
              confirmLabel: "Reset sign-in",
            })
          )
            act.mutate({ kind: "rotate", d });
        },
      }, {
        label: "Disable…",
        icon: PowerOff,
        onSelect: async () => {
          if (
            await confirm({
              title: `Disable “${label}”?`,
              message: "The TV stops showing reports straight away and says it was disabled by IT. You can enable it again at any time from this menu.",
              confirmLabel: "Disable screen",
              tone: "danger",
            })
          )
            act.mutate({ kind: "disable", d });
        },
      });
    if (d.status === "disabled") items.push(divider, { label: "Enable", icon: Power, onSelect: () => act.mutate({ kind: "enable", d }) });
    if (d.status === "active" || d.status === "disabled")
      items.push({
        label: "Revoke access…",
        icon: Ban,
        danger: true,
        onSelect: async () => {
          if (
            await confirm({
              title: `Revoke access for “${label}”?`,
              message: "For a TV that was lost, stolen, sold or replaced. It's signed out and can't be switched back on. To let that TV join again later, delete it from the list.",
              confirmLabel: "Revoke access",
              tone: "danger",
            })
          )
            act.mutate({ kind: "revoke", d });
        },
      });
    items.push(divider, { label: "Delete…", icon: Trash2, danger: true, onSelect: () => void confirmDelete([d]) });
    return items;
  };

  const focusApprove = () => {
    codeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    codeRef.current?.focus();
  };

  const onApproved = (o: ApproveOutcome) => {
    setApproving(null);
    const name = deviceLabel(o.device);
    if (o.kind === "reconnect") toast.success("Signed back in", `“${name}” picks up its reports within a few seconds.`);
    else if (o.kind === "replace") toast.success("Screen replaced", `The TV is now “${name}” in ${o.groupName || "its group"}. The old entry was removed.`);
    else toast.success("TV approved", `“${name}” joins ${o.groupName || "its group"} and switches to its reports within a few seconds.`);
  };

  const recentWaiting = waiting.filter((d) => codeIsLive(d) || ageMs(d.updated_at) < DAY_MS);
  const olderWaiting = waiting.filter((d) => !recentWaiting.includes(d));
  const visibleWaiting = showOlderPending ? waiting : recentWaiting;
  const selectedRows = all.filter((d) => selected.has(d.id));
  const allVisibleSelected = rows.length > 0 && rows.every((d) => selected.has(d.id));
  const cleanupCount = useMemo(() => {
    const s = cleanupSets(all);
    return new Set([...s.stalePending, ...s.revoked, ...s.duplicates, ...s.notSeen].map((d) => d.id)).size;
  }, [all]);
  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const subtitle = devices.isSuccess
    ? `${plural(approved, "approved screen")} · ${counts.online} online now${waiting.length ? ` · ${waiting.length} waiting for approval` : ""}.`
    : "Approve new TVs and tidy up the ones you no longer use.";

  return (
    <div className="ds-page">
      <PageHeader
        title="TV screens"
        subtitle={subtitle}
        actions={
          <>
            <Button icon={Layers} onClick={() => navigate("/admin/device-groups")}>
              Screen groups
            </Button>
            <Button icon={RefreshCw} loading={devices.isFetching && !devices.isLoading} onClick={() => void devices.refetch()}>
              Refresh
            </Button>
          </>
        }
      />

      <div className="ds-stack" style={{ gap: 24 }}>
        <Card title="Approve a TV" subtitle="Open the Maridive app on the TV (or the Reports screen in a laptop browser). It shows a 6-character code.">
          {groups.isError ? (
            <Banner tone="danger" title="Screen groups couldn't be loaded">
              {errorText(groups.error)}
            </Banner>
          ) : (
            <ApproveForm groups={groups.data} devices={all} layout="page" codeRef={codeRef} onDone={onApproved} />
          )}
        </Card>

        {devices.isError ? (
          <Banner
            tone={loadFailed ? "danger" : "warning"}
            title={loadFailed ? "TV screens couldn't be loaded" : "Couldn't refresh the list — showing what was loaded last"}
            action={<Button size="sm" icon={RefreshCw} onClick={() => void devices.refetch()}>Try again</Button>}
          >
            {errorText(devices.error)}
          </Banner>
        ) : null}

        {devices.isLoading ? (
          <section>
            <SectionTitle title="Waiting for approval" />
            <div className="ds-grid ds-grid--auto">
              {[0, 1, 2].map((i) => (
                <Card key={i}>
                  <Skeleton width="50%" height={16} />
                  <Skeleton width="80%" style={{ marginTop: 14 }} />
                  <Skeleton width="65%" style={{ marginTop: 8 }} />
                </Card>
              ))}
            </div>
          </section>
        ) : waiting.length ? (
          <section>
            <SectionTitle
              title={`Waiting for approval (${waiting.length})`}
              hint={
                liveCodes
                  ? `${liveCodes} TV${liveCodes === 1 ? " is" : "s are"} showing a code right now. Codes last 10 minutes.`
                  : "No TV is showing a valid code right now. A TV on the Reports screen shows a fresh code by itself."
              }
            />
            {visibleWaiting.length ? (
              <div className="ds-grid ds-grid--auto">
                {visibleWaiting.map((d) => (
                  <WaitingCard key={d.id} d={d} onApprove={() => setApproving(d)} onDelete={() => void confirmDelete([d])} />
                ))}
              </div>
            ) : (
              <p className="ds-muted" style={{ margin: 0 }}>No TV has asked to join in the last 24 hours.</p>
            )}
            {olderWaiting.length ? (
              <div className="ds-row" style={{ marginTop: 12, flexWrap: "wrap" }}>
                <Button variant="ghost" size="sm" icon={showOlderPending ? ChevronUp : ChevronDown} onClick={() => setShowOlderPending((v) => !v)}>
                  {showOlderPending ? "Hide older requests" : `Show ${plural(olderWaiting.length, "older request")}`}
                </Button>
                <Button variant="ghost" size="sm" icon={Eraser} onClick={() => setCleanupOpen(true)}>
                  Clean up old requests…
                </Button>
                {!showOlderPending ? <span className="ds-small ds-faint">From TVs that last asked more than a day ago — usually switched off or already set up again.</span> : null}
              </div>
            ) : null}
          </section>
        ) : null}

        {loadFailed ? null : (
          <section>
            <SectionTitle title="All screens" hint="Online = checked in within the last 15 minutes. Laptops are screens too: each browser address counts as one." />
            {all.length ? (
              <div className="ds-toolbar">
                <Segmented<Filter>
                  value={filter}
                  onChange={setFilter}
                  options={[
                    { value: "all", label: `All ${counts.all}` },
                    { value: "online", label: `Online ${counts.online}` },
                    { value: "offline", label: `Offline ${counts.offline}`, title: "Approved but not checked in recently (includes screens that are signed out)" },
                    { value: "waiting", label: `Waiting ${counts.waiting}` },
                    { value: "disabled", label: `Disabled ${counts.disabled}`, title: "Disabled or revoked" },
                  ]}
                />
                <Select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} style={{ width: 190 }} aria-label="Screen group">
                  <option value="">All screen groups</option>
                  {(groups.data ?? []).map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Select>
                <div className="ds-grow" />
                <SearchInput value={search} onChange={setSearch} placeholder="Search name, ID or IP address" width={260} />
                <Button icon={Eraser} onClick={() => setCleanupOpen(true)} title="Find old requests, revoked screens and duplicates">
                  Clean up{cleanupCount ? ` (${cleanupCount})` : ""}
                </Button>
              </div>
            ) : null}

            {selected.size ? (
              <div className="ds-card" role="region" aria-label="Selected screens" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", marginBottom: 10, boxShadow: "none", borderColor: "var(--accent)" }}>
                <strong>{plural(selected.size, "screen")} selected</strong>
                <span className="ds-small ds-muted ds-truncate" style={{ minWidth: 0 }}>
                  {selectedRows.slice(0, 3).map(deviceLabel).join(", ")}
                  {selectedRows.length > 3 ? ` and ${selectedRows.length - 3} more` : ""}
                </span>
                <div className="ds-grow" />
                <Button size="sm" variant="danger" icon={Trash2} loading={remove.isPending} onClick={() => void confirmDelete(selectedRows)}>
                  Delete…
                </Button>
                <Button size="sm" variant="ghost" icon={X} onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </div>
            ) : null}

            <Card flush>
              <DataTable<Device>
                rows={devices.data ? rows : undefined}
                loading={devices.isLoading}
                rowKey={(d) => d.id}
                empty={
                  all.length === 0 ? (
                    <EmptyState
                      icon={Tv}
                      title="No TV screens yet"
                      description="Install the Maridive app on a TV and open it. It will show a code — type that code in “Approve a TV” above."
                      action={<Button variant="primary" icon={CheckCircle2} onClick={focusApprove}>Approve a TV</Button>}
                    />
                  ) : groupFilter && !all.some((d) => d.device_group_id === groupFilter) ? (
                    <EmptyState
                      icon={Tv}
                      title={`No TVs in ${groupName.get(groupFilter) ?? "this group"} yet`}
                      description="Approve a TV above and choose this screen group for it, or move a screen here from its menu."
                      action={<Button onClick={() => setGroupFilter("")}>Show all groups</Button>}
                    />
                  ) : (
                    <EmptyState
                      icon={Search}
                      title="No screens match"
                      description="Try another filter, group or search."
                      action={
                        <Button
                          onClick={() => {
                            setFilter("all");
                            setGroupFilter("");
                            setSearch("");
                          }}
                        >
                          Clear filters
                        </Button>
                      }
                    />
                  )
                }
                columns={[
                  {
                    key: "select",
                    width: 40,
                    header: (
                      <label className="ds-check" title={allVisibleSelected ? "Clear selection" : "Select all shown"}>
                        <input
                          type="checkbox"
                          aria-label="Select all shown screens"
                          checked={allVisibleSelected}
                          onChange={(e) => setSelected((prev) => {
                            const next = new Set(prev);
                            rows.forEach((d) => (e.target.checked ? next.add(d.id) : next.delete(d.id)));
                            return next;
                          })}
                        />
                      </label>
                    ),
                    cell: (d) => (
                      <label className="ds-check">
                        <input type="checkbox" aria-label={`Select ${deviceLabel(d)}`} checked={selected.has(d.id)} onChange={(e) => toggle(d.id, e.target.checked)} />
                      </label>
                    ),
                  },
                  {
                    key: "name",
                    header: "Screen",
                    width: "28%",
                    sortValue: (d) => deviceLabel(d),
                    cell: (d) => (
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }} className={d.name ? "ds-row" : "ds-row ds-muted"}>
                          {groupName.get(d.device_group_id ?? "") === "Laptops" ? <Laptop size={14} className="ds-faint" /> : null}
                          <span className="ds-truncate">{deviceLabel(d)}</span>
                        </div>
                        <div className="ds-muted ds-small ds-truncate" title={`Device id ${d.device_uuid}`}>
                          ID <span className="ds-mono">{deviceShortId(d)}</span>
                          {d.screen_resolution ? ` · ${d.screen_resolution.replace("x", " × ")}` : ""}
                          {d.description ? ` · ${d.description}` : ""}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "group",
                    header: "Screen group",
                    sortValue: (d) => groupName.get(d.device_group_id ?? "") ?? "",
                    cell: (d) =>
                      d.device_group_id ? <span>{groupName.get(d.device_group_id) ?? "Unknown group"}</span> : <span className="ds-muted">Not assigned</span>,
                  },
                  {
                    key: "status",
                    header: "Status",
                    sortValue: (d) => STATE_ORDER[screenState(d)],
                    cell: (d) => {
                      const st = SCREEN_STATE[screenState(d)];
                      return (
                        <Badge tone={st.tone} dot title={st.hint}>
                          {st.label}
                        </Badge>
                      );
                    },
                  },
                  {
                    key: "seen",
                    header: "Last seen",
                    sortValue: (d) => d.last_seen_at ?? "",
                    cell: (d) =>
                      d.last_seen_at ? <span title={formatDateTime(d.last_seen_at)}>{formatRelative(d.last_seen_at)}</span> : <span className="ds-muted" title={`Added ${formatDateTime(d.created_at)}`}>Never</span>,
                  },
                  {
                    key: "ip",
                    header: "IP address",
                    sortValue: (d) => d.last_ip ?? "",
                    cell: (d) => (d.last_ip ? <span className="ds-mono">{d.last_ip}</span> : <span className="ds-muted">—</span>),
                  },
                  {
                    key: "app",
                    header: "App",
                    sortValue: (d) => d.app_version ?? "",
                    cell: (d) => (
                      <span className="ds-muted" title={d.tizen_version && d.tizen_version !== "unknown" ? `Tizen ${d.tizen_version}` : undefined}>
                        {d.app_version ?? "—"}
                      </span>
                    ),
                  },
                  {
                    key: "actions",
                    header: "",
                    width: 56,
                    align: "right",
                    cell: (d) => <Menu trigger={(p) => <Button {...p} size="sm" variant="ghost" icon={MoreHorizontal} aria-label={`Actions for ${deviceLabel(d)}`} />} items={menuFor(d)} />,
                  },
                ]}
              />
            </Card>
          </section>
        )}
      </div>

      <Modal
        open={!!approving}
        onClose={() => setApproving(null)}
        size="lg"
        title={approving && wantsReconnect(approving) ? `Sign “${deviceLabel(approving)}” back in` : "Approve this TV"}
        description="Look at the TV — it shows a 6-character code. Type it below to confirm it's the right screen."
      >
        {approving ? (
          <div className="ds-stack">
            <div className="ds-row ds-small ds-muted" style={{ gap: 8, flexWrap: "wrap" }}>
              <Badge>ID {deviceShortId(approving)}</Badge>
              {approving.screen_resolution ? <Badge>{approving.screen_resolution.replace("x", " × ")}</Badge> : null}
              {approving.app_version ? <Badge>App {approving.app_version}</Badge> : null}
              {approving.last_ip ? <Badge>{approving.last_ip}</Badge> : null}
              <span>asked {formatRelative(approving.updated_at)}</span>
            </div>
            {!codeIsLive(approving) ? (
              <Banner tone="warning" title="This TV's code has probably expired">
                Codes last 10 minutes. The TV shows a new one by itself while it's on the Reports screen — type the code you see now.
              </Banner>
            ) : null}
            <ApproveForm groups={groups.data} devices={all} expected={approving} layout="modal" onDone={onApproved} onCancel={() => setApproving(null)} />
          </div>
        ) : null}
      </Modal>

      <EditScreenModal device={editing?.d ?? null} focus={editing?.focus ?? "name"} groups={groups.data ?? []} onClose={() => setEditing(null)} />

      <CleanupModal
        open={cleanupOpen}
        all={all}
        onClose={() => setCleanupOpen(false)}
        onReview={(ids) => {
          setCleanupOpen(false);
          setFilter("all");
          setGroupFilter("");
          setSearch("");
          setSelected(new Set(ids));
        }}
        onDelete={(list) => {
          setCleanupOpen(false);
          void confirmDelete(list);
        }}
      />
    </div>
  );
}

function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <h2 style={{ margin: 0, fontSize: "var(--font-lg)", fontWeight: 650 }}>{title}</h2>
      {hint ? <p className="ds-small ds-muted" style={{ margin: "2px 0 0" }}>{hint}</p> : null}
    </div>
  );
}
